import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { Device } from '@ionic-native/device';
import { APP_CONFIG } from '../../environment';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { FeatureFlagTypes } from '../../model/types/feature-flag-types';
import {
  IInternalIssueReportData,
  IInternalIssueReportInput,
  InternalIssueScreenType,
} from '../../model/models/internal-issue-report/internal-issue-report.interface';
import { SegmentProvider } from '../analytics/segment';
import { RouteProvider } from '../route/route';
import { LogRocketProvider } from '../log-rocket/log-rocket';
import { UtilsProvider } from '../myria/utils-provider';
import { AppVersionProvider } from '../app-version/app-version';
import { HttpProvider } from '../http/http';
import { VueProvider } from '../vue/vue';
import { VueBridgeState } from '../vue/vue-helpers';
import { LoggerProvider } from '../logger/logger';

export const UNKNOWN_SCREEN = 'Unknown';

export interface IInternalIssueScreen {
  screen: string;
  screenType: InternalIssueScreenType;
}

/**
 * Builds and sends the "Internal Issue Reported" Segment event used by internal testers.
 * Everything except the tester's own input (category, severity, free text) is captured here
 * so the report can be traced back to a screen, a LogRocket session and a build.
 */
@Injectable()
export class InternalIssueReportProvider {

  constructor(
    private segmentProvider: SegmentProvider,
    private routeProvider: RouteProvider,
    private logRocketProvider: LogRocketProvider,
    private utilsProvider: UtilsProvider,
    private appVersionProvider: AppVersionProvider,
    private vueProvider: VueProvider,
    private platform: Platform,
    private device: Device,
  ) { }

  async isEnabled(): Promise<boolean> {
    return !!(await this.utilsProvider.hasFeatureFlag(FeatureFlagTypes.InternalIssueReporterEnabled));
  }

  getCurrentScreen(): IInternalIssueScreen {
    if (VueBridgeState.isUserInsideVueBridge && this.vueProvider.currentVueRoute)
      return { screen: this.vueProvider.currentVueRoute, screenType: 'rmt5' };

    let screen: string = null;
    try {
      screen = this.routeProvider.getLastActivePage();
    } catch (ex) {
      // no active nav yet (e.g. during bootstrap); fall through to the unknown marker
    }
    return {
      screen: screen || UNKNOWN_SCREEN,
      screenType: this.routeProvider.getPageType() === 'modal' ? 'modal' : 'page',
    };
  }

  async buildReport(input: IInternalIssueReportInput): Promise<IInternalIssueReportData> {
    const { screen, screenType } = this.getCurrentScreen();
    return {
      category: input.category,
      severity: input.severity,
      additionalText: (input.additionalText || '').trim(),
      screen,
      screenType,
      previousScreen: this.routeProvider.getPreviousPageName() || '',
      logRocketSessionURL: this.logRocketProvider.getSegmentSessionURL() || '',
      appVersion: await this.appVersionProvider.getVersionNumber(),
      environment: APP_CONFIG.env,
      platform: this.getPlatformName(),
      osVersion: this.device.version || '',
      deviceModel: this.device.model || '',
      language: HttpProvider.currentLanguage,
      country: APP_CONFIG.region.defaultCountryFrom,
      reportedAt: new Date().toISOString(),
    };
  }

  async send(input: IInternalIssueReportInput): Promise<void> {
    const analyticsData = await this.buildReport(input);
    LoggerProvider.info(this, 'Internal issue reported', analyticsData);
    await this.segmentProvider.trackEvent({ eventName: AnalyticsEventType.InternalIssueReported, analyticsData });
  }

  private getPlatformName(): string {
    if (!this.platform.is('cordova'))
      return 'browser';
    return this.platform.is('ios') ? 'ios' : 'android';
  }
}



// WEBPACK FOOTER //
// ./src/providers/internal-issue-report/internal-issue-report.ts